import { Capacitor } from "@capacitor/core";
import { Share as CapShare } from "@capacitor/share";
import * as React from "react";
import {
  MoreHorizontal,
  Pin,
  PinOff,
  FolderInput,
  Trash2,
  CopyPlus,
  Share2,
  Archive,
  Lock,
  Merge,
  ClipboardCopy,
  FileText,
  FileCode,
  Link2,
  Info,
} from "lucide-react";
import { htmlToMarkdown } from "@/lib/markdown";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import type { Note } from "@workspace/api-client-react";
import { useListFolders } from "@workspace/api-client-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface NoteActionsMenuProps {
  note: Note;
  onTogglePin: (id: number) => void;
  onMoveTo: (id: number) => void;
  onDelete: (id: number) => void;
  onArchive: (id: number) => void;
  onDuplicate: (id: number) => void;
  onMergeNotes: (id: number) => void;
  isPinPending?: boolean;
  isDeletePending?: boolean;
  isArchivePending?: boolean;
  isDuplicatePending?: boolean;
}

// Single source of truth for the note "..." menu. Used by both the
// Browser page and the Pinned page — add new actions here and both
// pages get them automatically.
export function NoteActionsMenu({
  note,
  onTogglePin,
  onMoveTo,
  onDelete,
  onArchive,
  onDuplicate,
  onMergeNotes,
  isPinPending,
  isDeletePending,
  isArchivePending,
  isDuplicatePending,
}: NoteActionsMenuProps) {
  const [copiedItem, setCopiedItem] = React.useState<
    "text" | "markdown" | "link" | null
  >(null);

  const flashCopied = (key: "text" | "markdown" | "link") => {
    setCopiedItem(key);
    setTimeout(() => {
      setCopiedItem((prev) => (prev === key ? null : prev));
    }, 1500);
  };

  const [infoOpen, setInfoOpen] = React.useState(false);

  const stripHtml = (html: string) =>
    html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

  const handleCopyText = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const text = [note.title, stripHtml(note.content)]
      .filter(Boolean)
      .join("\n\n");
    await navigator.clipboard.writeText(text);
    flashCopied("text");
  };

  const handleCopyMarkdown = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const md = [note.title ? `# ${note.title}` : "", htmlToMarkdown(note.content)]
      .filter(Boolean)
      .join("\n\n");
    await navigator.clipboard.writeText(md);
    flashCopied("markdown");
  };

  const handleCopyLink = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const base = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");
    const url = `${window.location.origin}${base}/browse/${note.id}`;
    await navigator.clipboard.writeText(url);
    flashCopied("link");
  };

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={(e) => e.stopPropagation()}
          aria-label="More options"
        >
          <MoreHorizontal className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        collisionPadding={8}
        className="rounded-2xl p-1 w-40 shadow-lg border-none space-y-0.5 max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto"
      >
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            onTogglePin(note.id);
          }}
          disabled={isPinPending}
          className="rounded-lg px-2.5 py-1.5 gap-2 text-[13px] font-medium bg-muted/60 focus:bg-muted"
        >
          {((note as any).isPinned ?? (note as any).pinned) ? (<><PinOff className="h-3.5 w-3.5" />Unpin</>) : (<><Pin className="h-3.5 w-3.5" />Pin</>)}
          
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            onMoveTo(note.id);
          }}
          className="rounded-lg px-2.5 py-1.5 gap-2 text-[13px] font-medium bg-muted/60 focus:bg-muted"
        >
          <FolderInput className="h-3.5 w-3.5" />
          Move to
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate(note.id);
          }}
          disabled={isDuplicatePending}
          className="rounded-lg px-2.5 py-1.5 gap-2 text-[13px] font-medium bg-muted/60 focus:bg-muted"
        >
          <CopyPlus className="h-3.5 w-3.5" />
          Duplicate
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={async (e) => {
            e.stopPropagation();
            const tmp = document.createElement("div");
            tmp.innerHTML = (note.content || "").replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div|li|h[1-6])>/gi, "\n");
            const body = (tmp.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
            const title = (note.title || "").trim();
            const text = [title, body].filter(Boolean).join("\n\n");
            if (!text) return;
            try {
              if (Capacitor.isNativePlatform()) await CapShare.share({ title: title || "Note", text });
              else if (navigator.share) await navigator.share({ title: title || "Note", text });
              else await navigator.clipboard.writeText(text);
            } catch {}
          }}
          className="rounded-lg px-2.5 py-1.5 gap-2 text-[13px] font-medium bg-muted/60 focus:bg-muted"
        >
          <Share2 className="h-3.5 w-3.5" />
          Share
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            onArchive(note.id);
          }}
          disabled={isArchivePending}
          className="rounded-lg px-2.5 py-1.5 gap-2 text-[13px] font-medium bg-muted/60 focus:bg-muted"
        >
          <Archive className="h-3.5 w-3.5" />
          Archive
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={(e) => e.stopPropagation()}
          className="rounded-lg px-2.5 py-1.5 gap-2 text-[13px] font-medium bg-muted/60 focus:bg-muted"
        >
          <Lock className="h-3.5 w-3.5" />
          Move Private
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            onMergeNotes(note.id);
          }}
          className="rounded-lg px-2.5 py-1.5 gap-2 text-[13px] font-medium bg-muted/60 focus:bg-muted"
        >
          <Merge className="h-3.5 w-3.5" />
          Merge Notes
        </DropdownMenuItem>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            onClick={(e) => e.stopPropagation()}
            className="rounded-lg px-2.5 py-1.5 gap-2 text-[13px] font-medium bg-muted/60 focus:bg-muted data-[state=open]:bg-muted"
          >
            <ClipboardCopy className="h-3.5 w-3.5" />
            Copy
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="rounded-2xl p-1 w-40 shadow-lg border-none space-y-0.5">
            <DropdownMenuItem
              onClick={handleCopyText}
              className="rounded-lg px-2.5 py-1.5 gap-2 text-[13px] font-medium bg-muted/60 focus:bg-muted"
            >
              <FileText className="h-3.5 w-3.5" />
              {copiedItem === "text" ? "Copied!" : "Copy text"}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleCopyMarkdown}
              className="rounded-lg px-2.5 py-1.5 gap-2 text-[13px] font-medium bg-muted/60 focus:bg-muted"
            >
              <FileCode className="h-3.5 w-3.5" />
              {copiedItem === "markdown" ? "Copied!" : "Copy as Markdown"}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleCopyLink}
              className="rounded-lg px-2.5 py-1.5 gap-2 text-[13px] font-medium bg-muted/60 focus:bg-muted"
            >
              <Link2 className="h-3.5 w-3.5" />
              {copiedItem === "link" ? "Copied!" : "Copy link"}
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            setInfoOpen(true);
          }}
          className="rounded-lg px-2.5 py-1.5 gap-2 text-[13px] font-medium bg-muted/60 focus:bg-muted"
        >
          <Info className="h-3.5 w-3.5" />
          Info
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            onDelete(note.id);
          }}
          disabled={isDeletePending}
          className="rounded-lg px-2.5 py-1.5 gap-2 text-[13px] font-medium text-red-500 focus:text-red-600"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    {infoOpen && (
      <span onClick={(e) => e.stopPropagation()}>
        <NoteInfoDialog note={note} onClose={() => setInfoOpen(false)} />
      </span>
    )}
    </>
  );
}

function NoteInfoDialog({ note, onClose }: { note: Note; onClose: () => void }) {
  const { data: folders } = useListFolders();
  const tmp = document.createElement("div");
  tmp.innerHTML = (note.content || "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/(p|div|li|h[1-6])>/gi, " ");
  const text = (tmp.textContent || "").replace(/\s+/g, " ").trim();
  const words = text ? text.split(" ").length : 0;
  const n = note as any;
  const fmt = (iso?: string | null) =>
    iso
      ? new Date(iso).toLocaleString(undefined, {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
        })
      : "-";
  const folderName =
    (Array.isArray(folders)
      ? (folders as any[]).find((f) => f.id === note.folderId)?.name
      : null) || "None";
  const status =
    [n.pinned ? "Pinned" : "", n.archivedAt ? "Archived" : ""]
      .filter(Boolean)
      .join(" · ") || "Normal";
  const rows: [string, string][] = [
    ["Folder", folderName],
    ["Created", fmt(note.createdAt)],
    ["Last updated", fmt(n.updatedAt || note.createdAt)],
    ["Words", String(words)],
    ["Characters", String(text.length)],
    ["Type", "Text"],
    ["Status", status],
  ];
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="w-[88vw] max-w-sm rounded-2xl">
        <DialogHeader>
          <DialogTitle>Note info</DialogTitle>
        </DialogHeader>
        <div className="divide-y divide-border/50 text-[13px]">
          {rows.map(([label, value]) => (
            <div key={label} className="flex items-center justify-between gap-4 py-2">
              <span className="text-muted-foreground">{label}</span>
              <span className="text-right font-medium">{value}</span>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
